import { useState } from 'react'
import { Box, Typography, Stack, Tabs, Tab, Chip, CircularProgress, useMediaQuery } from '@mui/material'

import { CardWithIcon } from '../../Design/Cards'
import { getItemFromLocalStorage } from '../../common/LocalStorage'

import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import FilterListIcon from '@mui/icons-material/FilterList'

const STAGES = [
	{ key: 'contacts', label: `Contacts ${getItemFromLocalStorage('localCurrentYear')?.name || ''}`, color: '#83c4c1' },
	{ key: 'evenements', label: 'Évènements', color: '#fbc410' },
	{ key: 'inscrits', label: 'Inscrits', color: '#51cc5d' },
]

const KNOWN_EVENT_TYPES = {
	jpo: process.env.REACT_APP_EVENT_TYPE_JPO,
	ji: process.env.REACT_APP_EVENT_TYPE_JI,
	inter: process.env.REACT_APP_EVENT_TYPE_INTERVENTION,
	mission: process.env.REACT_APP_EVENT_TYPE_MISSIONLYCEE,
	forum: process.env.REACT_APP_EVENT_TYPE_FORUM,
	phoning: process.env.REACT_APP_EVENT_TYPE_PHONING,
}

const PATHS = [
	{
		label: 'Évènements',
		filter: event => Object.values(KNOWN_EVENT_TYPES).includes(event?.type_id),
		subFilters: [
			{ label: 'Tous', match: null },
			{ label: 'JPO', match: 'jpo' },
			{ label: 'JI', match: 'ji' },
			{ label: 'Inter.', match: 'inter' },
			{ label: 'Mission', match: 'mission' },
			{ label: 'Forum', match: 'forum' },
			{ label: 'Phoning', match: 'phoning' },
			{ label: 'Autre', match: '__other__' },
		],
	},
	{
		label: 'Contacts entrants',
		filter: event => event?.name?.toLowerCase()?.startsWith('contacts entrants'),
		subFilters: [
			{ label: 'Tous', match: null },
			{ label: 'Hippocast', match: 'hippocast' },
			{ label: 'Nomad', match: 'nomad' },
			{ label: 'Thotis', match: 'thotis' },
			{ label: 'Diplomeo', match: 'diplomeo' },
			{ label: 'Hodea', match: 'hodea' },
			{ label: 'Studyrama', match: 'studyrama' },
			{ label: 'Dem. de brochures', match: 'brochures' },
			{ label: 'Dem. de rappel', match: 'rappel' },
			{ label: 'Q. Parcoursup', match: 'parcoursup' },
			{ label: 'Autre', match: '__other__' },
		],
	},
]

const KNOWN_CE_SOURCES = ['hippocast', 'nomad', 'thotis', 'diplomeo', 'hodea', 'studyrama', 'brochures', 'rappel', 'parcoursup']

const formatPercent = (value, total) => {
	if ( !total || !value ) return '–'
	const pct = value / total * 100
	return `${pct.toFixed(pct < 1 ? 3 : 1)}%`
}

const getFirstEvent = (lead) => {
	if ( !lead.lead_events?.length ) return null
	return lead.lead_events
		.map(le => le.event)
		.filter(e => e?.date)
		.sort((a, b) => new Date(a.date) - new Date(b.date))[0] || null
}

const matchesCeSubFilter = (event, subFilter) => {
	if ( !subFilter ) return true
	const name = event?.name?.toLowerCase() || ''
	if ( subFilter === '__other__' ) {
		return !KNOWN_CE_SOURCES.some(source => name.includes(source))
	}
	return name.includes(subFilter)
}

const ConversionFunnel = ({ leadsData, filteredDate, loading }) => {
	const isMobile = useMediaQuery(
		theme => theme.breakpoints.down('md'),
		{ noSsr: true }
	)
	const [activeTab, setActiveTab] = useState(0)
	const [subFilter, setSubFilter] = useState(null)
	const [originOnly, setOriginOnly] = useState(false)

	const filtered = leadsData || []

	const activePath = PATHS[activeTab]
	const isContactsEntrants = activeTab === 1

	const matchesSubFilter = (event) => {
		if ( !subFilter ) return true
		if ( isContactsEntrants ) return matchesCeSubFilter(event, subFilter)
		if ( subFilter === '__other__' ) {
			return !Object.values(KNOWN_EVENT_TYPES).includes(event?.type_id)
		}
		return event?.type_id === KNOWN_EVENT_TYPES[subFilter]
	}

	const hasMatchingEvent = (lead, forceOrigin = originOnly) => {
		if ( forceOrigin ) {
			const firstEvent = getFirstEvent(lead)
			if ( !firstEvent ) return false
			if ( filteredDate && new Date(firstEvent.date) < new Date(filteredDate) ) return false
			if ( isContactsEntrants ) {
				if ( !firstEvent?.name?.toLowerCase()?.startsWith('contacts entrants') ) return false
				return matchesCeSubFilter(firstEvent, subFilter)
			}
			if ( !activePath.filter(firstEvent) ) return false
			return matchesSubFilter(firstEvent)
		}
		return lead.lead_events?.some(le => {
			const event = le.event
			if ( !activePath.filter(event) ) return false
			if ( filteredDate && event?.date && new Date(event.date) < new Date(filteredDate) ) return false
			return matchesSubFilter(event)
		})
	}

	const contacts = filtered.length
	const originCount = filtered.filter(lead => hasMatchingEvent(lead, true)).length
	const anyCount = filtered.filter(lead => hasMatchingEvent(lead, false)).length
	const inscrits = filtered.filter(
		lead => hasMatchingEvent(lead, false) && lead.status_inscription_ids !== null
	).length

	const counts = [contacts, anyCount, inscrits]
	const globalRate = formatPercent(inscrits, contacts)

	return (
		<CardWithIcon
			to="/leads"
			icon={FilterListIcon}
			title="Entonnoir de conversion (phase de test)"
			subtitle={loading ? <CircularProgress size="15px" /> : globalRate}
			color="#83c4c1"
		>
			<Box sx={{ p: 0 }}>
				<Stack
					direction={isMobile ? 'column' : 'row'}
					alignItems="center"
					sx={{ mb: 0 }}
				>
					{/* Contacts — 30% */}
					<Box sx={{ flex: 2, textAlign: 'center', py: 1 }}>
						<Typography variant="h5" fontWeight="bold">
							{loading ? <CircularProgress size="20px" /> : contacts}
						</Typography>

						<Typography variant="body2" color="textSecondary">
							{STAGES[0].label}
						</Typography>
					</Box>

					{/* Divider between Contacts and Évènements */}
					<Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', mx: 0, ...( isMobile && { width: '100%', height: '1px', my: 1 }) }} />

					{/* Évènements + Inscrits — 70% */}
					<Box sx={{ flex: 7 }}>
						<Tabs
							value={activeTab}
							onChange={(e, v) => {
								setActiveTab(v)
								setSubFilter(null)
							}}
							centered={!isMobile}
							variant={isMobile ? 'scrollable' : 'standard'}
							scrollButtons="auto"
							sx={{ backgroundColor: '#f4f4f4', minHeight: 36, '& .MuiTab-root': { minHeight: 36, py: 0, textTransform: 'none' } }}
						>
							{PATHS.map((path, i) => (
								<Tab key={i} label={path.label} />
							))}
						</Tabs>

						<Stack direction="row" spacing={0.5} sx={{ mt: 1, flexWrap: 'wrap', gap: 0.5, justifyContent: 'center', alignItems: 'center' }}>
							{activePath.subFilters.map(sub => (
								<Chip
									key={sub.label}
									label={sub.label}
									size="small"
									sx={{ fontSize: '0.7rem', height: 22 }}
									variant={subFilter === sub.match ? 'filled' : 'outlined'}
									color={subFilter === sub.match ? 'primary' : 'default'}
									onClick={() => setSubFilter(sub.match)}
								/>
							))}
						</Stack>

						<Stack
							direction={isMobile ? 'column' : 'row'}
							alignItems="center"
							sx={{ mt: 1 }}
						>
							{/* Box A: Origine */}
							<Box
								onClick={() => setOriginOnly(true)}
								sx={{ flex: 1, textAlign: 'center', py: 1, cursor: 'pointer', opacity: originOnly ? 1 : 0.3, transition: 'opacity 0.2s' }}
							>
								<Typography variant="h5" fontWeight="bold">
									{loading ? <CircularProgress size="20px" /> : originCount}
								</Typography>

								<Typography variant="body2" color="textSecondary">
									Origine
									{subFilter && ` ${activePath.subFilters.find(s => s.match === subFilter)?.label || ''}`}
								</Typography>

								<Typography variant="body2" fontWeight="bold" sx={{ color: STAGES[1].color }}>
									{loading ? '–' : formatPercent(originCount, contacts)}
								</Typography>
							</Box>

							<Box sx={{ width: '1px', alignSelf: 'stretch', backgroundColor: 'divider', mx: 0.5, ...(isMobile && { width: '100%', height: '1px', my: 1 }) }} />

							{/* Box B: En cours de parcours */}
							<Box
								onClick={() => setOriginOnly(false)}
								sx={{ flex: 1, textAlign: 'center', py: 1, cursor: 'pointer', opacity: originOnly ? 0.3 : 1, transition: 'opacity 0.2s' }}
							>
								<Typography variant="h5" fontWeight="bold">
									{loading ? <CircularProgress size="20px" /> : anyCount}
								</Typography>

								<Typography variant="body2" color="textSecondary">
									Origine ou en cours de parcours
								</Typography>

								<Typography variant="body2" fontWeight="bold" sx={{ color: STAGES[1].color }}>
									{loading ? '–' : formatPercent(anyCount, contacts)}
								</Typography>
							</Box>

							<ArrowForwardIcon sx={{ color: 'text.disabled', mx: 0.5, ...(isMobile && { transform: 'rotate(90deg)' }) }} />

							{/* Box C: Inscrits */}
							<Box sx={{ flex: 1, textAlign: 'center', py: 1 }}>
								<Typography variant="h5" fontWeight="bold">
									{loading ? <CircularProgress size="20px" /> : inscrits}
								</Typography>

								<Typography variant="body2" color="textSecondary">
									{inscrits <= 1 ? 'Inscrit' : 'Inscrits'}
								</Typography>

								<Typography variant="body2" fontWeight="bold" sx={{ color: STAGES[2].color }}>
									{loading ? '–' : formatPercent(inscrits, anyCount)}
								</Typography>

								{inscrits > 0 && contacts > 0 && (
									<Typography variant="caption" sx={{ color: 'text.disabled' }}>
										{loading ? '–' : `${formatPercent(inscrits, contacts)} du total des contacts`}
									</Typography>
								)}
							</Box>
						</Stack>
					</Box>
				</Stack>

				<Stack direction="row" sx={{ height: 4, borderRadius: 2, overflow: 'hidden' }}>
					{contacts > 0 && STAGES.map((stage, i) => (
						<Box
							key={stage.key}
							sx={{
								flex: counts[i],
								backgroundColor: stage.color,
							}}
						/>
					))}
					{contacts === 0 && (
						<Box sx={{ flex: 1, backgroundColor: 'action.disabledBackground' }} />
					)}
				</Stack>
			</Box>
		</CardWithIcon>
	)
}

export default ConversionFunnel
